import { useState, useCallback, useEffect } from 'react';

/**
 * Hook that persists filter state in localStorage per user.
 * Filters survive page navigation and browser refresh.
 * Each page gets its own storage key.
 */
export function usePersistedFilters<T extends Record<string, unknown>>(
  storageKey: string,
  defaults: T,
): [T, (updates: Partial<T>) => void, () => void] {
  const fullKey = `elbklar_filters_${storageKey}`;

  const [filters, setFilters] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(fullKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new filter keys
        return { ...defaults, ...parsed };
      }
    } catch {
      // Ignore parse errors
    }
    return defaults;
  });

  // Persist to localStorage whenever filters change
  useEffect(() => {
    try {
      localStorage.setItem(fullKey, JSON.stringify(filters));
    } catch {
      // Ignore storage errors (e.g. quota exceeded)
    }
  }, [fullKey, filters]);

  const updateFilters = useCallback((updates: Partial<T>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaults);
    localStorage.removeItem(fullKey);
  }, [defaults, fullKey]);

  return [filters, updateFilters, resetFilters];
}
